/**
 * Hotel Validate Coupon Thunk
 *
 * Validates/applies/removes coupon via HOTEL_REVIEW_MCP API.
 * Uses MCP format with action_type="Validate-Coupon".
 * POST /hotels/validate-coupon
 *
 * Called when:
 * 1. Initial load - if any coupon has autoApplicable=true
 * 2. User clicks on a coupon to apply/remove
 */
import { createAsyncThunk } from '@reduxjs/toolkit';
import {
  fetchHotelValidateCoupon as fetchHotelValidateCouponApi,
  HotelValidateCouponError,
  type PriceSummaryData,
  type HotelValidateCouponRequestParams,
} from '../../api';
import type { RootState, AppDispatch } from '@/store/store';
import { logger } from '@/shared/utils/logger';
import { getOrCreateSessionId } from '@/shared/utils';
import { getUUID } from '@/shared/utils/appCookies';

// ============================================
// Types
// ============================================

interface ValidateCouponResult {
  priceSummary: PriceSummaryData | null;
  statusMessage: string | null;
  ratePlanList: unknown[];
  /** The coupon code that is now applied (null if removed or none) */
  appliedCouponCode: string | null;
}

/** Input params for the thunk - MCP format */
export interface ValidateCouponThunkParams {
  /** Transaction key from booking review */
  txnKey: string;
  /** Coupon code to validate/apply */
  couponCode: string;
  /** Whether to remove the coupon */
  removeCoupon?: boolean | undefined;
  /** Hotel name */
  hotelName: string;
  /** Hotel ID */
  hotelId: string;
  /** City name */
  city?: string | undefined;
  /** City code (e.g., CTGOI for Goa) */
  cityCode?: string | undefined;
}

// ============================================
// Thunk
// ============================================

/**
 * Validate/apply/remove coupon
 * POST /hotels/validate-coupon
 *
 * Sends MCP payload with action_type="Validate-Coupon"
 */
export const validateCouponThunk = createAsyncThunk<
  ValidateCouponResult,
  ValidateCouponThunkParams,
  { state: RootState; dispatch: AppDispatch; rejectValue: string }
>(
  'hotels/validateCoupon',
  async (params, { rejectWithValue }) => {
    try {
      const sessionId = getOrCreateSessionId();
      const userId = getUUID();

      // Build MCP request params
      const requestParams: HotelValidateCouponRequestParams = {
        sessionId,
        userId,
        txnKey: params.txnKey,
        couponCode: params.couponCode,
        removeCoupon: params.removeCoupon,
        hotelName: params.hotelName,
        hotelId: params.hotelId,
        city: params.city,
        cityCode: params.cityCode,
      };

      const response = await fetchHotelValidateCouponApi(requestParams);

      // Handle API error response
      if (!response.success) {
        const errorMessage = response.error?.message ?? 'Coupon validation failed';
        logger.error('[ValidateCouponThunk] Coupon validation failed:', response.error);
        return rejectWithValue(errorMessage);
      }

      return {
        priceSummary: response.priceSummary,
        statusMessage: response.statusMessage,
        ratePlanList: response.ratePlanList,
        appliedCouponCode: response.appliedCouponCode ?? null,
      };
    } catch (error) {
      logger.error('[ValidateCouponThunk] Failed to validate coupon:', error);
      if (error instanceof HotelValidateCouponError) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue('Failed to validate coupon');
    }
  },
  {
    // Prevent duplicate API calls
    condition: (_, { getState }) => {
      const state = getState() as RootState;
      const { isCouponValidating } = state.hotels.bookingReview;
      return !isCouponValidating;
    },
  },
);
